import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import {
  type ProjectDetails,
  projectQueries,
  updateProject,
} from '@/lib/queries/project';
import { generateMutationKey } from '@/lib/queries/utils';

interface Props {
  projectDetails: ProjectDetails;
}

export const TogglePrImpact = ({ projectDetails }: Props): JSX.Element => {
  const [enablePrImpact, setEnablePrImpact] = useState<boolean>(
    projectDetails.prImpactEnabled,
  );
  const [enablePrAiAnalysis, setEnablePrAiAnalysis] = useState<boolean>(
    projectDetails.prAiAnalysisEnabled,
  );
  const { toast } = useToast();

  const { mutateAsync, isPending } = useMutation({
    mutationKey: generateMutationKey(
      projectQueries.detail(projectDetails.id),
      'update-pr-impact',
      true,
    ),
    mutationFn: async ({
      prImpact,
      prAiAnalysis,
    }: {
      prImpact: boolean;
      prAiAnalysis: boolean;
    }) => {
      await updateProject(projectDetails.id, {
        prImpactEnabled: prImpact,
        prAiAnalysisEnabled: prAiAnalysis,
      });
    },
    onSuccess: () =>
      toast({ title: 'configuration.projectManagement.prImpactUpdated' }),
  });

  return (
    <div className="space-y-4 mt-6">
      <div className="flex items-center space-x-2">
        <Switch
          id="enable-pr-impact"
          checked={enablePrImpact}
          onCheckedChange={() => {
            const newEnablePrImpact = !enablePrImpact;
            setEnablePrImpact(newEnablePrImpact);
            if (newEnablePrImpact) {
              void mutateAsync({
                prImpact: newEnablePrImpact,
                prAiAnalysis: enablePrAiAnalysis,
              });
            } else {
              setEnablePrAiAnalysis(false);
              void mutateAsync({
                prImpact: newEnablePrImpact,
                prAiAnalysis: false,
              });
            }
          }}
          disabled={isPending}
        />
        <Label htmlFor="enable-pr-impact">
          <FormattedMessage id="configuration.projectManagement.enablePrImpact" />
        </Label>
      </div>

      <TooltipProvider disableHoverableContent>
        <Tooltip delayDuration={100}>
          <TooltipTrigger asChild>
            <div className="flex items-center space-x-2 max-w-fit">
              <Switch
                id="enable-pr-ai-analysis"
                checked={enablePrAiAnalysis}
                onCheckedChange={() => {
                  const newEnablePrAiAnalysis = !enablePrAiAnalysis;
                  setEnablePrAiAnalysis(newEnablePrAiAnalysis);
                  void mutateAsync({
                    prImpact: enablePrImpact,
                    prAiAnalysis: newEnablePrAiAnalysis,
                  });
                }}
                disabled={!enablePrImpact || isPending}
              />
              <Label htmlFor="enable-pr-ai-analysis">
                <FormattedMessage id="configuration.projectManagement.enablePrAiAnalysis" />
              </Label>
            </div>
          </TooltipTrigger>
          {!enablePrImpact && (
            <TooltipContent side="right">
              <FormattedMessage id="configuration.projectManagement.enablePrAiAnalysisDisabled" />
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};
